import { Colors, Heading, Label, SelectOutlined, Text } from '@amelio/ui-library';

import { CALIBRATION_TITLE, CYCLE_SELECT_WIDTH, HEADER_LAYOUT } from '../calibrationStyles';

import type { CalibrationCycle } from '@/types/calibration';
import type { OptionProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { parseServerDay } from '@/utils/dateUtils';
import { toReaderLocale } from '@/utils/numberFormat';

const CYCLE_SELECT_ID = 'calibration-cycle-select';

/** Title and subtitle, shown in every state of the page — including the one with no cycle to
 * select. */
const CalibrationTitle = () => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'flex flex-col gap-1'}>
            <Heading
                level={1}
                color={Colors.NAVY}
                className={CALIBRATION_TITLE}
            >
                {t('calibration.title')}
            </Heading>

            <Text color={Colors.BASE_500}>{t('calibration.subtitle')}</Text>
        </div>
    );
};

export interface CalibrationHeaderProps {
    cycles: CalibrationCycle[];
    selectedCycleId: string;
    onCycleChange: (cycleId: string) => void;
    language: string;
}

/**
 * Page title, subtitle and the cycle selector — nothing else. The report's two bulk actions live
 * with the rows they act on, in the employee view's draft banner, rather than in a header that
 * offers them from views where they have nothing to touch.
 *
 * Title and subtitle are a plain `Heading`/`Text` pair rather than the library's `PageHeader`
 * card, so this report reads as one family with its two neighbours
 * (`CompetencyMatrix/components/ReportToolbar`, `Compensation/components/CompensationHeader`)
 * under the same "Rapports" menu.
 */
export const CalibrationHeader = ({
    cycles,
    selectedCycleId,
    onCycleChange,
    language,
}: CalibrationHeaderProps) => {
    const { t } = useAmelioTranslations();

    // No cycle, no selector: an empty dropdown would be a dead control.
    if (cycles.length === 0) {
        return (
            <div className={HEADER_LAYOUT}>
                <CalibrationTitle />
            </div>
        );
    }

    // Kept in the backend's own order (most recent cycle first), hence `sort='none'`: sorted
    // alphabetically, the list would open on a cycle title rather than on the recent cycle the
    // page itself defaults to.
    const cycleOptions: OptionProps[] = cycles.map((cycle) => {
        // A cycle ends on a calendar day, not at an instant: `parseServerDay` keeps that day
        // whole instead of sliding it to the day before for readers west of the offset it
        // was written in.
        const endDay = cycle.endDate ? parseServerDay(cycle.endDate) : undefined;

        return {
            value: cycle.id,
            label: cycle.title,
            description: endDay
                ? t('calibration.cycle.endDateLabel', {
                      date: new Intl.DateTimeFormat(toReaderLocale(language), {
                          dateStyle: 'long',
                      }).format(endDay),
                  })
                : t('calibration.cycle.noEndDate'),
        };
    });

    return (
        <div className={HEADER_LAYOUT}>
            <CalibrationTitle />

            <div className={'flex flex-col gap-1'}>
                <Label
                    htmlFor={CYCLE_SELECT_ID}
                    color={Colors.BASE_500}
                >
                    {t('calibration.cycle.selectLabel')}
                </Label>

                <div className={CYCLE_SELECT_WIDTH}>
                    <SelectOutlined
                        id={CYCLE_SELECT_ID}
                        options={cycleOptions}
                        value={selectedCycleId}
                        onChange={onCycleChange}
                        sort={'none'}
                        ariaLabel={t('calibration.cycle.selectLabel')}
                        dataTestId={'calibration-cycle-select'}
                    />
                </div>
            </div>
        </div>
    );
};
